/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { useContext, useState, useEffect, useRef } from "react";
import { IndexContext } from "../IndexProvider";
import { Modal } from "react-bootstrap"
import validate from 'validate.js'
import ROLES, { ROLES_DESCRIPTION } from "../consts/roles";
import SERVICES, { SERVICES_DESCRIPTION } from "../consts/services";

const QRCode = require('qrcode')

const accountNameConstraints = {
    accountName: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[\S\s]{1,50}$/
        }
    }
};

const accountKanaConstraints = {
    accountKana: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[\p{scx=Hiragana}\s/　/・/ー]{1,50}$/u
        }
    }
};

const passwordConstraints = {
    password: {
        presence: {
            allowEmpty: false
        }
    }
};

const confirmedPasswordConstraints = {
    confirmedPassword: {
        presence: {
            allowEmpty: false
        }
    }
};

const mfaConfirmCodeConstraints = {
    mfaConfirmCode: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[0-9]{6}$/
        }
    }
};

export default function LoginAccount() {

    const { pageLoading, accountDetails, selectedAccount, setSelectedAccount, updateAccount, selectedTenant, changePassword, setMessage, setAccountDetails, associateSoftwareToken, enableMfa, disableMfa } = useContext(IndexContext)

    const [modalShow, setModalShow] = useState(false)
    const [modalTitle, setModalTitle] = useState("")
    const [isPending, setIsPending] = useState(false)
    const [accountName, setAccountName] = useState(selectedAccount.account_name);
    const [accountKana, setAccountKana] = useState(selectedAccount.account_name_kana)
    const [maxDeviceNum, setMaxDeviceNum] = useState(selectedAccount.max_login_device)
    const [accountRole, setAccountRole] = useState(selectedAccount.role)
    const [accountServices, setAccountServices] = useState(selectedAccount.service_id_list)

    const [isAccountNameValid, setIsAccountNameValid] = useState(false)
    const [isAccountKanaValid, setIsAccountKanaValid] = useState(false)
    const [shouldShowAccountNameFeedback, setShouldShowAccountNameFeedback] = useState(false)
    const [shouldShowAccountKanaFeedback, setShouldShowAccountKanaFeedback] = useState(false)

    const [oldPassword, setOldPassword] = useState("")
    const [password, setPassword] = useState("")
    const [confirmedPassword, setConfirmedPassword] = useState("");

    const [isOldPasswordValid, setIsOldPasswordValid] = useState(false)
    const [isPasswordValid, setIsPasswordValid] = useState(false)
    const [isConfirmedPasswordValid, setIsConfirmedPasswordValid] = useState(false)

    const [shouldShowOldPasswordFeedback, setShouldShowOldPasswordFeedback] = useState(false)
    const [shouldShowPasswordFeedback, setShouldShowPasswordFeedback] = useState(false)
    const [shouldShowConfirmedPasswordFeedback, setShouldShowConfirmedPasswordFeedback] = useState(false)

    const [mfaConfirmCode, setMfaConfirmCode] = useState("")
    const [isMfaConfirmCodeValid, setIsMfaConfirmCodeValid] = useState(false)
    const [shouldShowMfaConfirmCodeFeedback, setShouldShowMfaConfirmCodeFeedback] = useState(false)

    let canvasRef = useRef()

    useEffect(() => {
        setOldPassword("")
        setPassword("")
        setConfirmedPassword("")
    }, [])

    useEffect(() => {
        if (password !== confirmedPassword || validate({ confirmedPassword: confirmedPassword }, confirmedPasswordConstraints)) {
            setIsConfirmedPasswordValid(false)
        } else {
            setIsConfirmedPasswordValid(true)
        }
    }, [confirmedPassword])

    const buildFormControlClasses = (shouldShowFeedback, isValid) => {
        if (shouldShowFeedback) {
            return `form-control ${isValid ? "is-valid" : "is-invalid"}`
        } else {
            return "form-control"
        }
    }

    const onChangePasswordBtnClick = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        let count = 0

        if (validate({ password: oldPassword }, passwordConstraints)) {
            setIsOldPasswordValid(false)
        } else {
            setIsOldPasswordValid(true)
            count++
        }

        if (validate({ password: password }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
            count++
        }

        if (validate({ confirmedPassword: password }, confirmedPasswordConstraints)) {
            setIsConfirmedPasswordValid(false)
        } else {
            setIsConfirmedPasswordValid(true)
            count++
        }

        if (count === 3) {
            setIsPending(true)
            await changePassword(oldPassword, password)
            setIsPending(false)
            setModalShow(false)
        }
    }

    const onOldPasswordChange = (event) => {
        setOldPassword(event.target.value)
        setShouldShowOldPasswordFeedback(true)
        if (validate({ password: event.target.value }, passwordConstraints)) {
            setIsOldPasswordValid(false)
        } else {
            setIsOldPasswordValid(true)
        }
    }

    const onPasswordChange = (event) => {
        setPassword(event.target.value)
        setShouldShowPasswordFeedback(true)
        if (validate({ password: event.target.value }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
        }
    }

    const onConfirmedPasswordChange = (event) => {
        setConfirmedPassword(event.target.value)
        setShouldShowConfirmedPasswordFeedback(true)
    }

    const onAccountNameChange = (event) => {
        setAccountName(event.target.value)
        setShouldShowAccountNameFeedback(true)
        if (validate({ accountName: event.target.value }, accountNameConstraints)) {
            setIsAccountNameValid(false)
        } else {
            setIsAccountNameValid(true)
        }
    }

    const onAccountKanaChange = (event) => {
        setAccountKana(event.target.value)
        setShouldShowAccountKanaFeedback(true)
        if (validate({ accountKana: event.target.value }, accountKanaConstraints)) {
            setIsAccountKanaValid(false)
        } else {
            setIsAccountKanaValid(true)
        }
    }

    const onMaxDeviceNumChange = (event) => {
        setMaxDeviceNum(event.target.value)
    }

    const onAccountRoleChange = (event) => {
        setAccountRole(event.target.value)
    }

    const onRulerlessWebSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS)
        }
        setAccountServices(services)
    }

    const onRulerlessIosSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_IOS
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_IOS)
        }
        setAccountServices(services)
    }

    const onRulerlessIosOfflineSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_IOS_OFFLINE
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_IOS_OFFLINE)
        }

        setAccountServices(services)
    }

    const onRulerlessTruckBaggageSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_TRUCK_BAGGAGE
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_TRUCK_BAGGAGE)
        }
        setAccountServices(services)
    }
    

    const onMfaConfirmCodeChange = (event) => {
        setMfaConfirmCode(event.target.value)
        setShouldShowMfaConfirmCodeFeedback(true)
        if (validate({ mfaConfirmCode: event.target.value }, mfaConfirmCodeConstraints)) {
            setIsMfaConfirmCodeValid(false)
        } else {
            setIsMfaConfirmCodeValid(true)
        }
    }


    const onAccountEditBtnClick = async (e) => {
        setIsPending(true)
        let account = Object.assign({}, selectedAccount);
        account.account_name = accountName
        account.account_name_kana = accountKana

        if (accountDetails.role === ROLES.SYSTEM_ADMIN) {
            account.max_login_device = parseInt(maxDeviceNum)
            account.role = accountRole
            account.service_id_list = accountServices
        }

        await updateAccount(account)
        setIsPending(false)
        setModalShow(false)
        setSelectedAccount(account)
    }

    const onAccountStatusChangeBtnClick = async (e) => {
        setIsPending(true)
        let account = Object.assign({}, selectedAccount);
        account.status = !account.status;
        await updateAccount(account)
        setIsPending(false)
        setModalShow(false)
        setSelectedAccount(account)
    }

    const onVerifySoftwareTokenBtnClick = async () => {
        setIsPending(true)
        let account = Object.assign({}, accountDetails)
        try {
            await enableMfa(mfaConfirmCode)
            account.mfaState = true
            setAccountDetails(account)
            setMessage({ variant: "Success", msg: "MFA有効化しました。", show: true })
            setModalShow(false)
            setIsPending(false)
        } catch (err) {
            setMessage({ variant: "Warning", msg: "MFA有効化できませんでした。", show: true })
            setModalShow(false)
            setIsPending(false)
        }
    }

    const onMfaStateChangeBtnClick = async (e) => {
        let account = Object.assign({}, accountDetails)
        if (!account.mfaState) {
            setModalShow(true)
            setModalTitle("MFA有効化")
            try {
                let res = await associateSoftwareToken()
                let canvas = canvasRef.current
                if (canvas && res.data) {
                    let url = res.data.replace("issuer=Cognito-TOTP-MFA", "issuer=Rulerless")
                    QRCode.toCanvas(canvas, url, function (error) {
                        if (error) console.error(error)
                        console.log('success!');
                    })
                } else {
                    setMessage({ variant: "warning", msg: "QRコード発行できませんでした。", show: true })
                    setModalShow(false)
                }
            } catch (err) {
                setMessage({ variant: "warning", msg: "QRコード発行できませんでした。", show: true })
                setModalShow(false)
            }
        } else {
            setModalShow(true)
            setModalTitle("MFA無効化")
        }
    }

    const onDisableMfaBtnBtnClick = async (e) => {
        setIsPending(true)
        let account = Object.assign({}, accountDetails)
        try {
            await disableMfa()
            account.mfaState = false
            setAccountDetails(account)
            setMessage({ variant: "Success", msg: "MFA無効化しました。", show: true })
            setModalShow(false)
            setIsPending(false)
        } catch (err) {
            setMessage({ variant: "Warning", msg: "MFA無効化できませんでした。", show: true })
            setModalShow(false)
            setIsPending(false)
        }
    }

    const createModalBody = () => {
        if (modalTitle === "アカウント無効化" || modalTitle === "アカウント有効化") {
            const actionTitle = (selectedAccount.status ? '無効化' : '有効化')
            return (
                <div>{`アカウント名：${selectedAccount.account_name}を${actionTitle}します。宜しいですか？`}</div>
            )
        } else if (modalTitle === "アカウント削除") {
            return (
                <div>{`アカウント名：${selectedAccount.account_name}を削除します。宜しいですか？`}</div>
            )
        } else if (modalTitle === "アカウント変更") {
            return (
                <>
                    <div className="mb-3 row" id="accountNameInput">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="accountNameInput">アカウント名</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowAccountNameFeedback, isAccountNameValid)} onChange={onAccountNameChange} value={accountName} type="text" />
                            <div className="invalid-feedback">
                                アカウント名を入力してください。
                            </div>
                        </div>
                    </div>
                    <div className="mb-3 row" id="accountKanaInput">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="accountKanaInput">アカウント名（かな）</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowAccountKanaFeedback, isAccountKanaValid)} onChange={onAccountKanaChange} value={accountKana} type="text" />
                            <div className="invalid-feedback">
                                アカウント名（かな）を入力してください。
                            </div>
                        </div>
                    </div>
                    {
                        !(accountDetails.role === ROLES.SYSTEM_ADMIN && accountDetails.account_id !== selectedAccount.account_id) ? "" :
                            <>
                                <div className="mb-3 row" id="maxDeviceNumInput">
                                    <div className="col-12 col-sm-2 d-flex align-items-center">
                                        <label htmlFor="maxDeviceNumInput">最大デバイス数</label>
                                    </div>
                                    <div className="col-12 col-sm-10">
                                        <input className="form-control" type="number" value={maxDeviceNum} onChange={onMaxDeviceNumChange} min="1" max="10" />
                                    </div>
                                </div>
                                <div className="mb-3 row" id="accountRoleInput">
                                    <div className="col-12 col-sm-2 d-flex align-items-center">
                                        <label htmlFor="accountRoleInput">ロール</label>
                                    </div>
                                    <div className="col-12 col-sm-10">
                                        <select className="form-select" value={accountRole} onChange={onAccountRoleChange}>

                                            {
                                                [ROLES.GENERAL_USER, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN].map((role, idx) => {
                                                    if (idx === 0) {
                                                        return <option value={role} active="true" key={idx}>{ROLES_DESCRIPTION[role]}</option>
                                                    } else {
                                                        return (
                                                            <option value={role} key={idx}>{ROLES_DESCRIPTION[role]}</option>
                                                        )
                                                    }

                                                })
                                            }
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3 row" id="accountServicesInput">
                                    <div className="col-12 col-sm-2 d-flex align-items-center" >
                                        <label htmlFor="accountServicesInput">使用サービス</label>
                                    </div>
                                    <div className="col-12 col-sm-10 ">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" role="switch" id="rulerlessWebSwitch" checked={accountServices.includes(SERVICES.RULERLESS) ? true : false} onChange={onRulerlessWebSwitchChange} />
                                            <label className="form-check-label" htmlFor="rulerlessWebSwitch">Rulerless Web</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" role="switch" id="rulerlessIosSwitch" checked={accountServices.includes(SERVICES.RULERLESS_IOS) ? true : false} onChange={onRulerlessIosSwitchChange} />
                                            <label className="form-check-label" htmlFor="rulerlessIosSwitch">Rulerless iOS</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" checked={accountServices.includes(SERVICES.RULERLESS_IOS_OFFLINE) ? true : false} type="checkbox" role="switch" id="rulerlessIosOfflineSwitch" onChange={onRulerlessIosOfflineSwitchChange} />
                                            <label className="form-check-label" htmlFor="rulerlessIosOfflineSwitch">{"Rulerless iOS(Offline)"}</label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" role="switch" id="rulerlessTruckBaggageSwitch" checked={accountServices.includes(SERVICES.RULERLESS_TRUCK_BAGGAGE) ? true : false} onChange={onRulerlessTruckBaggageSwitchChange} />
                                            <label className="form-check-label" htmlFor="rulerlessTruckBaggageSwitch">Rulerless 積載率機能</label>
                                        </div>
                                    </div>
                                </div>
                            </>
                    }

                </>
            )
        } else if (modalTitle === "パスワード変更") {
            return (
                <>
                    <div className="mb-3" id="oldPasswordInput">
                        <label htmlFor="oldPasswordInput">旧パスワード</label>
                        <input className={buildFormControlClasses(shouldShowOldPasswordFeedback, isOldPasswordValid)} onChange={onOldPasswordChange} value={oldPassword} type="password" required />
                        <div className="invalid-feedback">
                            旧パスワードを入力してください。
                        </div>
                    </div>
                    <div className="mb-3" id="passwordInput">
                        <label htmlFor="passwordInput">新パスワード</label>
                        <input className={buildFormControlClasses(shouldShowPasswordFeedback, isPasswordValid)} onChange={onPasswordChange} value={password} type="password" required />
                        <div className="invalid-feedback">
                            新パスワードを入力してください。
                        </div>
                    </div>

                    <div className="mb-3" id="passwordInput">
                        <label htmlFor="passwordInput">新パスワード（確認）</label>
                        <input className={buildFormControlClasses(shouldShowConfirmedPasswordFeedback, isConfirmedPasswordValid)} onChange={onConfirmedPasswordChange} value={confirmedPassword} type="password" required />
                        <div className="invalid-feedback" >
                            入力されたパスワードが一致しません。
                        </div>
                    </div>
                </>
            )
        } else if (modalTitle === "MFA有効化") {
            return (
                <div>
                    <canvas id="canvas" ref={canvasRef}></canvas>
                    <div className="mb-3" id="mfaConfirmCodeInput">
                        <label htmlFor="mfaConfirmCodeInput">ワンタイムパスワード</label>
                        <input className={buildFormControlClasses(shouldShowMfaConfirmCodeFeedback, isMfaConfirmCodeValid)} onChange={onMfaConfirmCodeChange} value={mfaConfirmCode} type="text" required />
                        <div className="invalid-feedback">
                            英数字６桁を入力してください。
                        </div>
                    </div>
                </div>
            )
        } else if (modalTitle === "MFA無効化") {
            return (
                <div>{`アカウント名：${accountDetails.account_name}のMFA設定を無効化します。宜しいですか？`}</div>
            )
        }
    }

    const createModalFooter = () => {

        let actionBtn
        if (modalTitle === "アカウント無効化" || modalTitle === "アカウント有効化") {
            actionBtn = isPending ? <button className="btn btn-outline-warning" disabled >変更中...</button> : <button className="btn btn-outline-warning" onClick={onAccountStatusChangeBtnClick} >変更</button>
        } else if (modalTitle === "アカウント削除") {
            actionBtn = isPending ? <button className="btn btn-outline-danger" disabled >削除中...</button> : <button className="btn btn-outline-danger" onClick={onAccountDeleteBtnClick} >削除</button>
        } else if (modalTitle === "アカウント変更") {
            actionBtn = isPending ? <button className="btn btn-outline-danger" disabled >変更中...</button> : <button className="btn btn-outline-danger" onClick={onAccountEditBtnClick} >変更</button>
        } else if (modalTitle === "パスワード変更") {
            actionBtn = isPending ? <button className="btn btn-outline-primary" disabled >変更中...</button> : <button className="btn btn-outline-primary" onClick={onChangePasswordBtnClick} >変更</button>
        } else if (modalTitle === "MFA有効化") {
            actionBtn = isPending ? <button type="button" className="btn btn-primary mt-2" >確認中...</button> : <button type="button" className="btn btn-outline-primary mt-2" onClick={onVerifySoftwareTokenBtnClick}>確認</button>
        } else if (modalTitle === "MFA無効化") {
            actionBtn = isPending ? <button type="button" className="btn btn-primary mt-2" >変更中...</button> : <button type="button" className="btn btn-outline-primary mt-2" onClick={onDisableMfaBtnBtnClick}>無効化する</button>
        }

        const cancelBtn = isPending ? <button className="btn btn-outline-secondary ms-3" disabled >キャンセル</button> : <button className="btn btn-outline-secondary ms-3" onClick={(e) => setModalShow(false)} >キャンセル</button>

        return (
            <>
                {cancelBtn}
                {actionBtn}
            </>
        )
    }

    const createMainElement = (isSkeleton) => {
        return (
            <div className={"p-3" + (isSkeleton ? " skeleton-1" : "")} >
                <strong style={{ fontSize: 20, visibility: isSkeleton ? "hidden" : "visible" }} >アカウント詳細</strong>
                <div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center" >
                        <label htmlFor="tenantName" style={{ fontWeight: "bold" }}>組織名</label>
                    </div>
                    <div className="col-12 col-sm-10 ">
                        <input type="text" readOnly className="form-control-plaintext" value={selectedTenant.tenant_name} id="tenantName" />
                    </div>
                </div>
                <div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center" >
                        <label htmlFor="accountId" style={{ fontWeight: "bold" }}>アカウントID</label>
                    </div>
                    <div className="col-12 col-sm-10 ">
                        <input type="text" readOnly className="form-control-plaintext" value={selectedAccount.account_id} id="accountId" />
                    </div>
                </div>
                <div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center">
                        <label htmlFor="accountName" style={{ fontWeight: "bold" }}>アカウント名</label>
                    </div>
                    <div className="col-12 col-sm-10">
                        <input type="text" readOnly className="form-control-plaintext" value={selectedAccount.account_name} id="accountName" />
                    </div>
                </div>
                <div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center">
                        <label htmlFor="accountKana" style={{ fontWeight: "bold" }}>アカウント名（かな）</label>
                    </div>
                    <div className="col-12 col-sm-10">
                        <input type="text" readOnly className="form-control-plaintext" value={selectedAccount.account_name_kana} id="accountKana" />
                    </div>
                </div>
                {/*<div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center">
                        <label htmlFor="maxDeviceNumInput" style={{ fontWeight: "bold" }}>最大デバイス数</label>
                    </div>
                    <div className="col-12 col-sm-10">
                        <input type="text" readOnly className="form-control-plaintext" value={selectedAccount.max_login_device} id="maxDeviceNumInput" />
                    </div>
                </div>*/}
                <div className="mb-3 row" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center">
                        <label htmlFor="accountRoleInput" style={{ fontWeight: "bold" }}>ロール</label>
                    </div>
                    <div className="col-12 col-sm-10">
                        {
                            isSkeleton ?
                                <input className="form-control-plaintext" value="skeleton" />
                                :
                                <input type="text" readOnly className="form-control-plaintext" value={ROLES_DESCRIPTION[selectedAccount.role]} id="accountRoleInput" />
                        }
                    </div>
                </div>
                <div className="mb-5 row" id="accountServicesInput" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <div className="col-12 col-sm-2 d-flex align-items-center" >
                        <label htmlFor="accountServicesInput" style={{ fontWeight: "bold" }}>使用サービス</label>
                    </div>
                    <div className="col-12 col-sm-10 ">
                        {
                            isSkeleton ? "skeleton" : selectedAccount.service_id_list.map(s => SERVICES_DESCRIPTION[s]).join("、")
                        }
                    </div>
                </div>

                <div className="mb-5" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    {
                        isSkeleton ?
                            <>
                                <button className="btn btn-outline-primary me-3 mb-3" >アカウント変更</button>
                                <button className="btn btn-outline-primary me-3 mb-3" >パスワード変更</button>
                                <button className="btn btn-outline-primary me-3 mb-3">MFA無効化</button>

                                <button className="btn btn-outline-primary me-3 mb-3">無効化</button>
                            </>
                            :
                            <>
                                {
                                    accountDetails.account_id === selectedAccount.account_id || selectedAccount.role !== ROLES.GENERAL_USER ?
                                        <button className="btn btn-outline-primary me-3 mb-3" onClick={(e) => {
                                            setModalShow(true)
                                            setModalTitle("アカウント変更")
                                        }}>アカウント変更</button> : ""
                                }

                                {
                                    accountDetails.account_id === selectedAccount.account_id ?
                                        <>
                                            <button className="btn btn-outline-primary me-3 mb-3" onClick={onMfaStateChangeBtnClick}>
                                                {
                                                    accountDetails.mfaState === true ? "MFA無効化" : "MFA有効化"
                                                }
                                            </button>
                                            <button className="btn btn-outline-primary me-3 mb-3" onClick={(e) => {
                                                setModalShow(true)
                                                setModalTitle("パスワード変更")
                                            }}>パスワード変更</button>
                                        </>
                                        : ""
                                }

                                {/*
                                    accountDetails.account_id === selectedAccount.account_id ? "" : <button className="btn btn-outline-primary me-3 mb-3" onClick={(e) => {
                                        setModalTitle("アカウント" + (selectedAccount.status ? '無効化' : '有効化'))
                                        setModalShow(true)
                                    }}>{"アカウント" + (selectedAccount.status ? "無効化" : "有効化")}</button>
                                */}
                            </>
                    }

                </div>

                {
                    isSkeleton ?
                        <></>
                        :
                        <Modal size='lg' show={modalShow} onHide={() => setModalShow(false)} aria-labelledby='contained-modal-title-vcenter' centered >
                            <div className='ModalModal'>
                                <Modal.Header closeButton>
                                    <Modal.Title>{modalTitle}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    {
                                        createModalBody()
                                    }
                                </Modal.Body>
                                <Modal.Footer>
                                    {
                                        createModalFooter()
                                    }
                                </Modal.Footer>
                            </div>
                        </Modal>
                }
            </div>
        )
    }

    return pageLoading ? createMainElement(true) : createMainElement()
}
